// Which claims the browser's own idToken actually carried, reported to Tier B
// on the get-user request so the Cognito-side attribute list and the token-side
// claim list land on one correlated log line.
//
// Frank M. Booth, 2026-08-13: Maria Alfaro's header showed "AP Admin" with the
// name above it blank. That is `given_name` present and `name` absent — in the
// TOKEN, which is a different read of Cognito from the `adminGetUser` call Tier
// B makes, and the one nothing on the server can see. Whether the attribute is
// missing from Cognito itself or only from the issued token decides which fault
// we are chasing: a bad attribute write, or token issuance (app-client read
// scopes, the refresh flow, the SSO path). Without this half we would be
// guessing between them.
//
// Presence flags only — never claim values. This rides on an ordinary request
// header and lands in CloudWatch; there is no reason for it to carry anybody's
// email or name.

import { getPersistKey } from './tabSession';
import { clearSessionPreservingTab } from '../utils/tabSession';

export const TOKEN_CLAIMS_HEADER = 'X-Token-Claims';

// The claims the app actually depends on. `name` renders the header, and
// `given_name` is the role that drives every permission gate.
const REPORTED_CLAIMS = ['name', 'given_name', 'email'];

const decodeJwtPayload = (token) => {
  if (typeof token !== 'string') return null;
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  try {
    // base64url -> base64, then pad to a multiple of 4. atob rejects both the
    // URL alphabet and unpadded input.
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const parsed = JSON.parse(atob(padded));
    // Must be a claims object. An array also satisfies `typeof === 'object'`,
    // and would summarise as `name=0,given_name=0,email=0` — which reads as a
    // token that genuinely carried no claims, the opposite of what we would be
    // looking at. "unreadable" and "read it, the claim was missing" have to stay
    // distinguishable or this instrumentation answers the wrong question.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    // A malformed token must never break a profile fetch — this is diagnostic.
    // Non-ASCII claim values can also come back mangled through atob, which is
    // harmless precisely because we only ever test for presence.
    return null;
  }
};

const isPresent = (value) => typeof value === 'string' && value.trim() !== '';

// Decoding runs on every read of the role, from ~30 components, on every store
// update. Cached against the token string itself so a re-render costs a map
// lookup; a new token (login, or a different tab's session) misses and decodes
// once more.
let claimsCache = { token: null, claims: null };

/**
 * The claims of the session's own idToken, read from sessionStorage.
 *
 * The persisted redux `signIn` slice is written once at login and nothing
 * refreshes it, while the raw token sits in sessionStorage under its own key
 * with a different lifetime. When the slice is lost the token is routinely
 * still there — and it is the same token the slice was built from, so decoding
 * it recovers exactly what was lost without a round trip or a re-login.
 *
 * Returns null when there is no usable token, so callers can tell "no session"
 * from "session with an absent claim".
 */
export const claimsFromSessionToken = () => {
  let raw;
  try {
    raw = sessionStorage.getItem('token');
  } catch {
    // Storage throws in some private-browsing modes. No claims, not a crash.
    return null;
  }
  if (!raw) {
    claimsCache = { token: null, claims: null };
    return null;
  }
  if (claimsCache.token === raw) return claimsCache.claims;
  const decoded = decodeJwtPayload(raw);
  // An expired token must not answer questions about who the user is or what
  // they may do. The server already refuses one, so honouring it here would
  // only ever produce a UI that offers actions the next request rejects.
  // A token with no `exp` at all is not treated as expired: Cognito always
  // issues one, and inventing a failure for a shape we do not produce would
  // trade a real fallback for a hypothetical.
  const expired =
    typeof decoded?.exp === 'number' && decoded.exp * 1000 <= Date.now();
  const claims = expired ? null : decoded;
  claimsCache = { token: raw, claims };
  return claims;
};

// Sticky for the life of the page: set the first time identity has to be
// recovered from the raw token because the persisted slice could not answer.
//
// This is the signal the fix would otherwise destroy. Once the role resolves
// through the token, the user sees nothing wrong — no blank header, no missing
// Pay Control column — so nobody reports anything and the only remaining
// evidence that a session lost its store is this flag. Sticky rather than
// per-read because the interesting fact is "it happened at all in this
// session", and a later render resolving cleanly does not undo that.
let tokenFallbackUsed = false;

export const noteTokenFallback = () => {
  tokenFallbackUsed = true;
};

// Test seam: the cache is module-level and would otherwise leak between cases.
export const resetClaimsCache = () => {
  claimsCache = { token: null, claims: null };
  tokenFallbackUsed = false;
};

/**
 * `name=0,given_name=1,email=1`, or 'unreadable' when there is no usable token.
 * Defaults to the session's idToken — the same one signIn.jsx stores and whose
 * payload the header name used to be read from.
 */
export const summariseTokenClaims = (token) => {
  const raw = token === undefined ? sessionStorage.getItem('token') : token;
  const payload = decodeJwtPayload(raw);
  if (!payload) return 'unreadable';
  return REPORTED_CLAIMS.map((claim) => `${claim}=${isPresent(payload[claim]) ? 1 : 0}`).join(',');
};

/**
 * The same claims as the persisted redux `signIn` slice holds them.
 *
 * This is the half that names the cause. The header renders
 * `state.signIn.signIn.cognitoRes.idToken.payload.name` — the slice, not the
 * token — and redux-persist keeps that slice in sessionStorage under a per-tab
 * key, in an app with 81 `clearSessionPreservingTab()` call sites. So a blank header
 * has three possible explanations, and comparing the two sources separates them:
 *
 *   token name=0                  -> the issued token really lacked the claim
 *   token name=1, slice present=0 -> the persisted slice was lost or never
 *                                    rehydrated (clear / tab-key / quota)
 *   token name=1, slice name=0    -> the slice was written without the claim
 *
 * Cognito still holds `name` for every affected Frank M. Booth account, with
 * UserLastModifiedDate months old, so the first of those is already the least
 * likely and this is the comparison worth having.
 */
export const summariseSliceClaims = () => {
  let raw;
  try {
    raw = sessionStorage.getItem(`persist:${getPersistKey()}`);
  } catch {
    return 'unreadable';
  }
  if (!raw) return 'present=0';
  try {
    const persisted = JSON.parse(raw);
    // redux-persist stores each slice as its own JSON *string* inside the blob.
    const slice = JSON.parse(persisted?.signIn ?? 'null');
    const payload = slice?.signIn?.cognitoRes?.idToken?.payload;
    if (!payload || typeof payload !== 'object') return 'present=0';
    return `present=1,${REPORTED_CLAIMS.map(
      (claim) => `${claim}=${isPresent(payload[claim]) ? 1 : 0}`
    ).join(',')}`;
  } catch {
    return 'unreadable';
  }
};

/**
 * The single header value: `token[…] slice[…]`. Kept to one header so it rides
 * along on the request Tier B already logs, rather than needing an endpoint.
 */
export const summariseClaimSources = () =>
  `token[${summariseTokenClaims()}] slice[${summariseSliceClaims()}]` +
  ` recovered=${tokenFallbackUsed ? 1 : 0}`;
